/**
 * errores — madre ÚNICA de la explicación de errores al operador (D1, hoja UXP-4).
 *
 * El dolor: 24 ficheros pintaban Problem-Details crudos en toasts (jerga
 * rbac-dmn-granular / validation_resultado) — la voz de la app ante el primer
 * cliente era la del servidor. Aquí se traduce UNA vez: mensaje humano + acción
 * sugerida; lo técnico va al detalle (description del toast) y a la consola
 * AARPIA, jamás como titular.
 */
import { toast } from 'sonner'
import { ApiError } from '@/lib/api'
import { logAarpia } from '@/lib/log'

export interface ErrorExplicado {
  /** Titular humano, con acción sugerida cuando la hay. */
  mensaje: string
  /** El detalle técnico del servidor (para description plegada / consola). */
  detalleTecnico?: string
}

const esFalloRed = (e: any) =>
  !(e instanceof ApiError) && (e instanceof TypeError || /failed to fetch|networkerror|load failed|connect/i.test(String(e?.message || '')))

export function explicarError(e: unknown): ErrorExplicado {
  const err = e as any
  if (esFalloRed(err)) {
    return { mensaje: 'No hay conexión con el servidor — comprueba tu red y reintenta.', detalleTecnico: String(err?.message || err) }
  }
  if (!(e instanceof ApiError)) {
    return { mensaje: 'Algo falló de forma inesperada — reintenta.', detalleTecnico: String(err?.message || err) }
  }
  const tipo = String((e.raw as any)?.type || '')
  const detalle = e.detail || e.title || e.message
  switch (true) {
    case e.status === 401:
      return { mensaje: 'Tu sesión caducó — vuelve a iniciar sesión.', detalleTecnico: detalle }
    case e.status === 403 && /dmn/i.test(tipo + detalle):
      return { mensaje: 'La regla del proceso no lo permite en el estado actual.', detalleTecnico: detalle }
    case e.status === 403:
      return { mensaje: 'Tu permiso no alcanza para esta acción — pídela al administrador de tu organización si la necesitas.', detalleTecnico: detalle }
    case e.status === 404:
      return { mensaje: 'No se encontró — o no es visible desde tu organización actual.', detalleTecnico: detalle }
    case e.status === 409:
      return { mensaje: 'Otra operación lo cambió antes — recarga y vuelve a intentarlo.', detalleTecnico: detalle }
    case e.status === 400:
      return { mensaje: `Faltan datos o no son válidos: ${detalle}`, detalleTecnico: e.errors?.join(' · ') }
    case e.status >= 500:
      return { mensaje: 'El servidor tuvo un problema — reintenta en un momento.', detalleTecnico: detalle }
    default:
      return { mensaje: detalle, detalleTecnico: tipo || undefined }
  }
}

/** Toast estándar de error: titular humano + técnico plegado en description + consola AARPIA. */
export function toastError(e: unknown, contexto?: string): void {
  const { mensaje, detalleTecnico } = explicarError(e)
  toast.error(contexto ? `${contexto}: ${mensaje}` : mensaje, detalleTecnico ? { description: detalleTecnico } : undefined)
  if (detalleTecnico) logAarpia.warn(contexto || 'Error', '·', detalleTecnico)
}
